import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { AlertCircle, Check, File as FileIcon, Link as LinkIcon } from "lucide-react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AvatarAi } from "@/zapi-frontend/shared/ui/chat";
import { SyncedSpinner } from "@/zapi-frontend/shared/ui/SyncedSpinner";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { parseMessageContent } from "@/zapi-frontend/shared/ui/custom/ChatMessage/lib/parseMessageContent";
import type { ParsedContent } from "@/zapi-frontend/shared/ui/custom/ChatMessage/ChatMessage";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { cn } from "@/zapi-frontend/shared/lib/utils";

export type TDocumentReadStatus = "reading" | "read" | "unreadable";

export type TVaultItem = { kind: "file" | "link"; label: string; documentId?: number; status?: TDocumentReadStatus };

function DocumentReadStatus({ status }: { status: TDocumentReadStatus }) {
  const { t } = useTranslation("createProject");
  if (status === "reading") {
    return (
      <span className="flex items-center gap-1.5">
        <SyncedSpinner className="size-3" />
        <FigmaTypography component="span" variant="body-3" color="zinc600">
          {t("documentStatus.reading")}
        </FigmaTypography>
      </span>
    );
  }
  const read = status === "read";
  const Icon = read ? Check : AlertCircle;
  return (
    <span className={cn("flex items-center gap-1.5", read ? "text-innoveraGreen-700" : "text-innoveraYellow-700")}>
      <Icon className="size-3 shrink-0" aria-hidden />
      <FigmaTypography component="span" variant="body-3" className="text-inherit">
        {t(read ? "documentStatus.read" : "documentStatus.unreadable")}
      </FigmaTypography>
    </span>
  );
}

function parseVaultItemsMessage(content: string): TVaultItem[] | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed && parsed.type === "vault_items" && Array.isArray(parsed.items)) {
      return parsed.items as TVaultItem[];
    }
  } catch {
    // not JSON, treat as plain text
  }
  return null;
}

function parseDimensionAnswerMessage(content: string): string | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed && parsed.type === "dimension_answer" && typeof parsed.content === "string") {
      return parsed.content;
    }
  } catch {
    // not JSON, treat as plain text
  }
  return null;
}

function parseNavMarkerMessage(content: string): string | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed && parsed.type === "nav" && typeof parsed.content === "string") {
      return parsed.content;
    }
  } catch {
    // not JSON, treat as plain text
  }
  return null;
}

function LoadingDots() {
  return (
    <span className="inline-flex gap-1 py-2 items-center">
      <span className="size-1.5 rounded-full bg-innoveraZinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="size-1.5 rounded-full bg-innoveraZinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="size-1.5 rounded-full bg-innoveraZinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}

function boldQuestionLabel(content: string): string {
  return content.replace(/(?<!\*\*)\bQuestion:(?!\*\*)/g, "**Question:**");
}

function MessageMarkdown({ content, isError }: { content: string; isError?: boolean }) {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        a: ({ children, ...props }) => {
          const isInternal =
            props.href?.startsWith("/") ||
            props.href?.includes("platform.innovera") ||
            props.href?.includes("localhost");
          return (
            <a
              {...props}
              {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
              className={cn("underline", isError ? "text-innoveraRed-500" : "text-blue-500 hover:text-blue-600")}
            >
              {children}
            </a>
          );
        },
        p: (props) => <p {...props} className="mb-2 last:mb-0" />,
        ul: (props) => <ul {...props} className="list-disc pl-5 mb-2 space-y-1" />,
        ol: (props) => <ol {...props} className="list-decimal pl-5 mb-2 space-y-1" />,
        li: (props) => <li {...props} />,
        strong: (props) => <strong {...props} className="font-semibold" />
      }}
    >
      {boldQuestionLabel(content)}
    </ReactMarkdown>
  );
}

export const CaseBriefChatMessage = memo(function CaseBriefChatMessage({
  message,
  isLoading,
  progressMessage
}: {
  message: ChatMessageType;
  isLoading?: boolean;
  progressMessage?: string | null;
}) {
  const { isLabs } = useMeStore();
  const [parsedContent, setParsedContent] = useState<ParsedContent>({ type: "loading", content: "" });
  const [parseError, setParseError] = useState(false);

  useEffect(() => {
    if (message.role === "user") {
      setParsedContent({ type: "answer", content: message.content });
      setParseError(false);
      return;
    }

    if (isLoading) {
      setParsedContent({ type: "loading", content: "" });
      setParseError(false);
      return;
    }

    try {
      const parsed = parseMessageContent(message.content, !!isLabs);
      setParsedContent(parsed);
      setParseError(false);
    } catch {
      setParsedContent({
        type: "error",
        content: "There was a problem displaying this message."
      });
      setParseError(true);
    }
  }, [message, isLoading, isLabs]);

  if (message.role === "user") {
    const navMarker = parseNavMarkerMessage(message.content);

    if (navMarker) {
      return (
        <div className="flex justify-end px-4 py-3 pl-8">
          <div className="bg-innoveraZinc-100 max-w-[292px] px-[14px] py-[10px] rounded-lg rounded-br-[2px] text-[14px] leading-[22px] text-innoveraZinc-900 break-words whitespace-pre-wrap">
            {navMarker}
          </div>
        </div>
      );
    }

    const vaultItems = parseVaultItemsMessage(message.content);

    if (vaultItems) {
      return (
        <div className="flex justify-end px-4 py-3 pl-8">
          <div className="bg-innoveraZinc-100 max-w-[292px] w-[240px] px-[14px] py-[10px] rounded-lg rounded-br-[2px] flex flex-col gap-1.5">
            {vaultItems.map((item, idx) => (
              <div key={`${item.kind}-${idx}`} className="flex flex-col gap-0.5 w-full min-w-0">
                <div className="flex gap-2 items-center w-full min-w-0">
                  {item.kind === "link" ? (
                    <LinkIcon className="size-4 shrink-0 text-innoveraZinc-900" />
                  ) : (
                    <FileIcon className="size-4 shrink-0 text-innoveraZinc-900" />
                  )}
                  <FigmaTypography variant="body-2" color="zinc900" className="truncate">{item.label}</FigmaTypography>
                </div>
                {item.status && (
                  <div className="pl-6">
                    <DocumentReadStatus status={item.status} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const dimensionAnswer = parseDimensionAnswerMessage(message.content);

    return (
      <div className="flex justify-end px-4 py-3 pl-8">
        <div className="bg-innoveraZinc-100 max-w-[292px] px-[14px] py-[10px] rounded-lg rounded-br-[2px] text-[14px] leading-[22px] text-innoveraZinc-900 break-words whitespace-pre-wrap">
          {dimensionAnswer ?? message.content}
        </div>
      </div>
    );
  }

  const isErrorMessage = parsedContent.type === "error";

  return (
    <div className="flex gap-3 items-start px-4 py-3">
      <AvatarAi state={isErrorMessage || parseError ? "error" : "default"} />
      <div className="flex-1 min-w-0 pt-1">
        {isLoading ? (
          <div className="flex items-center gap-2" role="status">
            {progressMessage && <FigmaTypography variant="body-2" color="zinc500">{progressMessage}</FigmaTypography>}
            <LoadingDots />
          </div>
        ) : isErrorMessage || parseError ? (
          <div className="flex items-start gap-2 text-innoveraRed-500 text-[14px] leading-[22px]">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <MessageMarkdown content={parsedContent.content} isError />
            </div>
          </div>
        ) : (
          <div className="text-[14px] leading-[22px] text-innoveraZinc-900">
            <MessageMarkdown content={parsedContent.content} />
          </div>
        )}
      </div>
    </div>
  );
});
